import ReactECharts from 'echarts-for-react';
import benchmark from '../../data/promptEngineeringBenchmark.json';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * PromptVsMemoryChart — Raisy Memory vs four prompt-engineering strategies (blog12, Fig 1).
 *
 * Chart-type-first: five categorical scores against a reference mean → ECharts
 * vertical bar + markLine. Data reads from promptEngineeringBenchmark.json — the
 * single data file for the post (Rule 27). Brand teal for the Raisy arm, neutral
 * grey for the prompt-engineered arms, so identity is never colour-alone (the
 * arm is also named on the axis). Theme: `dark` prop override, otherwise
 * follows the live html class theme (Rule 29).
 * ONE shared formatter for bar labels, axis ticks, tooltip, and the mark line.
 */

const BRAND = {
  teal: '#0AB3BF',
  neutral: '#9CA3AF',
};

type ArmItem = { label: string; value: number; raisy: boolean };

const fmt = (v: number) => `${Math.round(v)}`;

export const PromptVsMemoryChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const chartHeight = height ?? (narrow ? 400 : 440);
  const fig = (
    benchmark as unknown as {
      figures: {
        arm_scores: {
          source: string;
          max_score: number;
          items: ArmItem[];
          prompt_engineering_average: number;
        };
      };
    }
  ).figures.arm_scores;

  const C = isDark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

  const items = fig.items;

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { marker?: string; name: string; value: number }[]) =>
        params
          .map(
            (p) =>
              `${p.marker} ${p.name}: <b>${fmt(p.value)}</b> / ${fig.max_score}`,
          )
          .join('<br/>'),
    },
    grid: { left: narrow ? 44 : 56, right: narrow ? 12 : 32, top: 40, bottom: narrow ? 88 : 76 },
    xAxis: {
      type: 'category',
      data: items.map((i) => i.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisLabel: {
        color: C.axis,
        // 5 categories in ~343px => ~68px each; 9px keeps the two-line label legible.
        fontSize: narrow ? 9 : 11,
        interval: 0,
        // Break the label so "System Prompt" sits on its own row.
        formatter: (v: string) => v.replace(/ System Prompt$/, '\nSystem Prompt'),
        lineHeight: 15,
      },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: fig.max_score,
      name: `score (max ${fig.max_score})`,
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: 12, formatter: (v: number) => fmt(v) },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.35 } },
    },
    series: [
      {
        name: 'Judge score',
        type: 'bar',
        barWidth: narrow ? '62%' : '52%',
        data: items.map((i) => ({
          value: i.value,
          itemStyle: {
            color: i.raisy ? BRAND.teal : BRAND.neutral,
            borderRadius: [6, 6, 0, 0],
          },
        })),
        label: {
          show: true,
          position: 'top',
          color: C.axis,
          fontWeight: 700,
          fontSize: narrow ? 11 : 13,
          formatter: (p: { value: number }) => fmt(p.value),
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: C.axis, type: 'dashed', width: 1.5 },
          label: {
            color: C.axis,
            position: 'insideEndTop',
            fontSize: narrow ? 9.5 : 11,
            formatter: () =>
              narrow
                ? `avg ${fmt(fig.prompt_engineering_average)}`
                : `Prompt-engineering average: ${fmt(fig.prompt_engineering_average)}`,
          },
          data: [{ yAxis: fig.prompt_engineering_average }],
        },
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height: chartHeight }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center`}>
        Figure 1. Output quality on the same task, model, temperature, and judge ({fig.source}).
        Four prompt-engineering strategies land within 15 points of each other with no ordering;
        structured memory scores {fig.items.find((i) => i.raisy)?.value} — {' '}
        {(fig.items.find((i) => i.raisy)?.value ?? 0) - fig.prompt_engineering_average} points above their average.
        Single sample per arm.
      </p>
    </div>
  );
};
